import {
  BarChart3, BookOpen, CalendarCheck, CalendarDays, ClipboardCheck, Compass, Image, Inbox, KanbanSquare, Megaphone, Package,
  PenLine, Settings, Settings2, Users, Wrench, type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  key: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** One plain sentence: shown in the page header and the command palette. */
  description: string;
  /** Name of the tab in Pranav's v10.346 prototype this page carries forward (for traceability). */
  prototypeTab?: string;
  adminOnly?: boolean;
}
export interface NavGroup { label: string; items: NavItem[]; }

export const NAV: NavGroup[] = [
  { label: 'Daily work', items: [
    { key: 'today', label: 'Today', path: '/today', icon: CalendarCheck, prototypeTab: 'Today', description: 'Who to contact today and what to say, ranked for you.' },
    { key: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDays, description: 'Every date for your people: contacts, promises, meetings, renewals and festivals.' },
  ] },
  { label: 'Sales', items: [
    { key: 'pipeline', label: 'Pipeline', path: '/pipeline', icon: KanbanSquare, prototypeTab: 'Pipeline', description: 'Where every person and deal stands right now.' },
    { key: 'capture', label: 'Capture', path: '/capture', icon: Inbox, prototypeTab: 'Capture', description: 'Add people and conversations from a chat, a card or a file.' },
    { key: 'draft', label: 'Draft', path: '/draft', icon: PenLine, prototypeTab: 'Draft', description: 'Write a message for a person, check it, copy it and send it yourself.' },
  ] },
  { label: 'Growth', items: [
    { key: 'reach', label: 'Reach', path: '/reach', icon: Megaphone, prototypeTab: 'Reach', description: 'Your people list, LinkedIn contacts and outreach.' },
    { key: 'media', label: 'Media', path: '/media', icon: Image, prototypeTab: 'Media', description: 'Press and media contacts, and who to approach next (at most two a day).' },
    { key: 'strategy', label: 'Strategy', path: '/strategy', icon: Compass, prototypeTab: 'Strategy', description: 'Your revenue goal, the plan to reach it, and the gaps.' },
  ] },
  { label: 'Knowledge and results', items: [
    { key: 'products', label: 'Products', path: '/products', icon: Package, prototypeTab: 'Products', description: 'What you sell, and the teaching notes for each product.' },
    { key: 'laws', label: 'Selling rules', path: '/laws', icon: BookOpen, prototypeTab: 'Laws', description: 'The rules that keep outreach polite and effective, and why.' },
    { key: 'reports', label: 'Reports', path: '/reports', icon: BarChart3, prototypeTab: 'Report', description: 'Focus report, wins and losses, and what we learned.' },
    { key: 'review', label: 'Review', path: '/review', icon: ClipboardCheck, prototypeTab: 'Review', description: 'Advisor reviews of your plan and facts only you know.' },
  ] },
  { label: 'Administration', items: [
    { key: 'setup', label: 'Business setup', path: '/setup', icon: Settings2, description: 'Who you are, what the AI knows, your rules, lists and timing, per business area.' },
    { key: 'tools', label: 'Tools', path: '/tools', icon: Wrench, prototypeTab: 'Tools', description: 'Clean-up, backups and data export.' },
    { key: 'users', label: 'Users', path: '/users', icon: Users, description: 'Colleagues who can sign in, and which business areas they see.' },
    { key: 'settings', label: 'Settings', path: '/settings', icon: Settings, description: 'Your profile, business areas and display.' },
  ] },
];

export const ALL_NAV: NavItem[] = NAV.flatMap((g) => g.items);

export function navFor(pathname: string): { group: NavGroup; item: NavItem } | null {
  for (const group of NAV) {
    for (const item of group.items) {
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) return { group, item };
    }
  }
  return null;
}

export function navItem(key: string): NavItem {
  const it = ALL_NAV.find((i) => i.key === key);
  if (!it) throw new Error(`unknown nav key ${key}`);
  return it;
}
